/*
 * TaskTooltipContent — the rich quick-description tooltip rendered on hover
 * over a reminder title. Used by both the /reminders dashboard and the
 * RemindersModal opened from student/teacher profiles, so both surfaces
 * show the same Due / Alerts / Description card.
 *
 * Pure presentation: caller resolves uuid → task and picks the timezone
 * (the dashboard uses the operator-wide CMS timezone; the profile modal
 * prefers the linked user's iana_timezone).
 */

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { label_by_uuid, department_by_uuid } from './api';

// taskTooltipStyle (the ReactTooltip wrapper styling) lives in ./constants.js
// alongside the rest of the reminders presentation constants — keeping it
// here would force this file to mix a React component with a value export,
// which Vite Fast Refresh disallows.

const offsetLabel = (mins) => {
  if (mins === 0) return 'At time of event';
  if (mins < 60) return `${mins} min before`;
  if (mins < 1440) {
    const h = mins / 60;
    return `${h % 1 === 0 ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'} before`;
  }
  if (mins < 10080) {
    const d = mins / 1440;
    return `${d % 1 === 0 ? d : d.toFixed(1)} day${d === 1 ? '' : 's'} before`;
  }
  const w = mins / 10080;
  return `${w % 1 === 0 ? w : w.toFixed(1)} week${w === 1 ? '' : 's'} before`;
};

// Always format with full time + timezone code so the tooltip reads the same
// regardless of has_time (the form always sends a real ISO timestamp,
// defaulting to a sane time when the user didn't pick one). Bracket is added
// outside moment's format string since moment uses `[ ]` as its own literal-
// escape syntax.
const formatInTz = (m, tz) => {
  const base = m.tz(tz).format('ddd, MMM D, YYYY, h:mm A');
  const zone = m.tz(tz).format('z');
  return `${base} [ ${zone} ]`;
};

const sectionLabelStyle = {
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontSize: 10
};

function TaskTooltipContent({ task, tz }) {
  if (!task) return null;
  const dueAtStr = formatInTz(moment(task.due_at), tz);
  const alertFireAt = (mins) => formatInTz(moment(task.due_at).subtract(mins, 'minutes'), tz);
  // Resolve uuids → display rows. Falsy entries (cache misses) are filtered
  // so a stale uuid doesn't render an empty pill.
  const labels = (task.label_uuids || []).map(label_by_uuid).filter(Boolean);
  const department = department_by_uuid(task.department_uuid);

  return (
    <div style={{ minWidth: 260 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#f8fafc' }}>
        {task.title}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11.5 }}>
        <div>
          <div style={sectionLabelStyle}>Due · {tz}</div>
          <div style={{ color: '#f1f5f9' }}>{dueAtStr}</div>
        </div>

        {Array.isArray(task.alerts) && task.alerts.length > 0 ? (
          <div>
            <div style={sectionLabelStyle}>Alerts · {tz}</div>
            {task.alerts.map((a) => (
              <div key={a.offset_minutes} style={{ color: '#f1f5f9' }}>
                {offsetLabel(a.offset_minutes)} — {alertFireAt(a.offset_minutes)}
              </div>
            ))}
          </div>
        ) : null}

        {labels.length > 0 ? (
          <div>
            <div style={sectionLabelStyle}>Labels</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
              {labels.map((l) => (
                <span
                  key={l.uuid}
                  style={{
                    background: l.color,
                    color: '#1e293b',
                    borderRadius: 999,
                    padding: '1px 7px',
                    fontSize: 10
                  }}
                >
                  {l.name}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {department ? (
          <div>
            <div style={sectionLabelStyle}>Department</div>
            <span
              style={{
                display: 'inline-block',
                background: department.color,
                color: '#1e293b',
                borderRadius: 999,
                padding: '1px 7px',
                fontSize: 10,
                marginTop: 2
              }}
            >
              {department.name}
            </span>
          </div>
        ) : null}

        {task.description && task.description.trim() ? (
          <div>
            <div style={sectionLabelStyle}>Description</div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e2e8f0' }}>
              {task.description}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

TaskTooltipContent.propTypes = {
  task: PropTypes.shape({
    title: PropTypes.string,
    due_at: PropTypes.string,
    description: PropTypes.string,
    alerts: PropTypes.arrayOf(PropTypes.shape({ offset_minutes: PropTypes.number })),
    label_uuids: PropTypes.arrayOf(PropTypes.string),
    department_uuid: PropTypes.string
  }),
  tz: PropTypes.string.isRequired
};

TaskTooltipContent.defaultProps = {
  task: null
};

export default TaskTooltipContent;
